import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Button, Typography, Chip, Collapse, Tooltip } from '@mui/material';
import { Add, CalendarToday, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface DateTimePickerProps { 
  suggestedTimes: string[]; // Array of UTC datetime strings
  onChange: (times: string[]) => void;
  blockedTimes?: string[]; // Array of UTC datetime strings that should be blocked/grayed out
}

interface DateGroup {
  date: string;
  selectedTimes: string[];
  availableHours: string[];
  displayDate: string;
}

export default function DateTimePicker({ suggestedTimes, onChange, blockedTimes = [] }: DateTimePickerProps) {
  const { t, i18n } = useTranslation();
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [additionalWeeks, setAdditionalWeeks] = useState(0); // Additional weeks beyond the base 3 weeks
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const getMinDate = () => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 2);
    return minDate.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const today = new Date();
    const currentWeekMonday = getMondayOfWeek(today);
    
    // Calculate 3 weeks from current Monday + any additional weeks
    const totalWeeksToShow = 3 + additionalWeeks;
    const maxDate = new Date(currentWeekMonday);
    maxDate.setDate(maxDate.getDate() + (totalWeeksToShow * 7) - 1); // -1 to include Sunday of the last week
    
    return maxDate.toISOString().split('T')[0];
  };

  // Helper function to get the Monday of a given date's week
  const getMondayOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  // Helper function to get ISO week number
  const getWeekNumber = (date: Date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Helper function to format week header
  const formatWeekHeader = (mondayDate: Date) => {
    const weekNumber = getWeekNumber(mondayDate);
    const sunday = new Date(mondayDate);
    sunday.setDate(sunday.getDate() + 6);
    
    const mondayStr = mondayDate.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
    const sundayStr = sunday.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
    
    return `${t('date_picker.week', 'Vecka')} ${weekNumber} (${mondayStr} - ${sundayStr})`;
  };

  const getAllHours = () => {
    // All hours in chronological order for expanded view
    return ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'];
  };

  const getPriorityHours = (date: string) => {
    if (!date) return [];
    
    const selectedDate = new Date(date);
    const dayOfWeek = selectedDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    if (isWeekend) {
      // Weekend: show main hours first (10-13)
      return ['10:00', '11:00', '12:00', '13:00'];
    } else {
      // Weekday: show after work hours first (16-19)
      return ['16:00', '17:00', '18:00', '19:00'];
    }
  };

  const formatDisplayDate = (dateString: string, language: string) => {
    const date = new Date(dateString);
    
    // Get full weekday name and capitalize first letter
    const weekdayName = date.toLocaleDateString(language, { weekday: 'long' });
    const capitalizedWeekday = weekdayName.charAt(0).toUpperCase() + weekdayName.slice(1);
    
    // Format as DD/MM
    const day = date.getDate();
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    
    return `${capitalizedWeekday} ${day}/${month}`;
  };

  // Generate all available dates from min to max, grouped by weeks
  const availableDates = useMemo(() => {
    const dates: DateGroup[] = [];
    const minDate = new Date(getMinDate());
    const maxDate = new Date(getMaxDate());
    
    for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
      const dateString = d.toISOString().split('T')[0];
      
      // Extract selected times for this date from UTC datetimes
      const selectedTimes = suggestedTimes
        .filter(time => {
          const timeDate = new Date(time);
          return timeDate.toISOString().split('T')[0] === dateString;
        })
        .map(time => {
          const timeDate = new Date(time);
          return timeDate.toLocaleTimeString('en-GB', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          });
        });
      
      dates.push({
        date: dateString,
        selectedTimes,
        availableHours: getAllHours(), // Always store all hours
        displayDate: formatDisplayDate(dateString, i18n.language)
      });
    }
    
    return dates;
  }, [suggestedTimes, i18n.language, additionalWeeks]);

  // Group dates by week (Monday to Sunday)
  const groupedByWeeks = useMemo(() => {
    const weeks: Array<{
      weekHeader: string;
      dates: DateGroup[];
      mondayDate: Date;
    }> = [];
    
    let currentWeekMonday: Date | null = null;
    let currentWeekDates: DateGroup[] = [];
    
    availableDates.forEach((dateGroup) => {
      const date = new Date(dateGroup.date);
      const weekMonday = getMondayOfWeek(date);
      
      // If this is a new week, save the previous week and start a new one
      if (!currentWeekMonday || weekMonday.getTime() !== currentWeekMonday.getTime()) {
        if (currentWeekMonday && currentWeekDates.length > 0) {
          weeks.push({
            weekHeader: formatWeekHeader(currentWeekMonday),
            dates: currentWeekDates,
            mondayDate: currentWeekMonday
          });
        }
        currentWeekMonday = weekMonday;
        currentWeekDates = [];
      }
      
      currentWeekDates.push(dateGroup);
    });
    
    // Don't forget the last week
    if (currentWeekMonday && currentWeekDates.length > 0) {
      weeks.push({
        weekHeader: formatWeekHeader(currentWeekMonday),
        dates: currentWeekDates,
        mondayDate: currentWeekMonday
      });
    }
    
    return weeks;
  }, [availableDates, i18n.language]);

  const toggleTimeSelection = (date: string, time: string) => {
    // Create local datetime first, then convert to UTC
    const localDateTime = new Date(`${date}T${time}:00`);
    const utcDateTimeString = localDateTime.toISOString();
    
    // Check if this time is blocked (already discussed)
    if (blockedTimes.includes(utcDateTimeString)) {
      return; // Don't allow selection of blocked times
    }
    
    // Check if this exact datetime already exists
    const existingTimeIndex = suggestedTimes.findIndex(st => st === utcDateTimeString);
    
    if (existingTimeIndex >= 0) {
      // Remove the time
      const updatedTimes = suggestedTimes.filter((_, index) => index !== existingTimeIndex);
      onChange(updatedTimes);
    } else {
      // Add the new UTC datetime
      onChange([...suggestedTimes, utcDateTimeString]);
    }
  };

  // Helper function to check if a time is blocked
  const isTimeBlocked = (date: string, time: string) => {
    const localDateTime = new Date(`${date}T${time}:00`);
    const utcDateTimeString = localDateTime.toISOString();
    return blockedTimes.includes(utcDateTimeString);
  };

  const toggleExpanded = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };



  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarToday sx={{ fontSize: '1.2rem' }} />
        {t('date_picker.title', 'När kan du träffas?')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
        {t('date_picker.subtitle', 'Välj 3-5 datum inom de kommande två veckorna som passar dig. Du kan välja flera tider per datum.')}
      </Typography>
      
      <Box 
        ref={scrollContainerRef}
        sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}
      >
        {groupedByWeeks.map((week) => (
          <Box key={week.mondayDate.toISOString()}>
            {/* Week header */}
            <Box sx={{ 
              mb: 2, 
              mt: week === groupedByWeeks[0] ? 0 : 3,
              borderBottom: '1px solid rgba(255,255,255,0.2)',
              pb: 1
            }}>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255,255,255,0.8)', 
                fontWeight: 'bold',
                fontSize: '1rem'
              }}>
                {week.weekHeader}
              </Typography>
            </Box>

            {/* Dates in this week */}
            {week.dates.map((dateGroup) => {
              const isExpanded = expandedDates.has(dateGroup.date);
              const priorityHours = getPriorityHours(dateGroup.date);
              const allHours = dateGroup.availableHours;
              const hasMoreHours = allHours.length > priorityHours.length;
              const hasSelectedTimes = dateGroup.selectedTimes.length > 0;
              
              return (
                <Box 
                  key={dateGroup.date} 
                  sx={{ 
                    mb: 1, 
                    p: 1.5, 
                    backgroundColor: hasSelectedTimes ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)', 
                    borderRadius: 2,
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  {/* Header */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.9rem', color: 'white' }}>
                      {dateGroup.displayDate}
                    </Typography>
                  </Box>

                  {/* Time slots */}
                  {!isExpanded ? (
                    /* Collapsed: Show priority times + expand button */
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {priorityHours.map((time) => {
                        const isSelected = dateGroup.selectedTimes.includes(time);
                        const isBlocked = isTimeBlocked(dateGroup.date, time);
                        
                        const chipElement = (
                          <Chip
                            key={time}
                            label={time}
                            size="small"
                            onClick={() => toggleTimeSelection(dateGroup.date, time)}
                            sx={{
                              bgcolor: isSelected ? 'white' : 'rgba(255,255,255,0.1)',
                              color: isSelected ? '#8E603C' : 'white',
                              '&:hover': {
                                bgcolor: isBlocked ? 'rgba(255,255,255,0.1)' :
                                        isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)',
                              },
                              cursor: isBlocked ? 'not-allowed' : 'pointer',
                              fontWeight: isSelected ? 'bold' : 'normal',
                              height: '28px',
                              opacity: isBlocked ? 0.5 : 1
                            }}
                          />
                        );

                        return isBlocked ? (
                          <Tooltip 
                            key={time}
                            title={t('date_picker.blocked_time_tooltip', 'Denna tid har redan diskuterats')}
                            arrow
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>{chipElement}</span>
                          </Tooltip>
                        ) : chipElement;
                      })}
                      
                      {/* Expand button */}
                      {hasMoreHours && (
                        <Chip
                          label={`+${allHours.length - priorityHours.length} more ▾`}
                          size="small"
                          onClick={() => toggleExpanded(dateGroup.date)}
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.8)',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.2)',
                            },
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            height: '28px'
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    /* Expanded: Show ALL times in chronological order + collapse button */
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {allHours.map((time) => {
                        const isSelected = dateGroup.selectedTimes.includes(time);
                        const isBlocked = isTimeBlocked(dateGroup.date, time);
                        
                        const chipElement = (
                          <Chip
                            key={time}
                            label={time}
                            size="small"
                            onClick={() => toggleTimeSelection(dateGroup.date, time)}
                            sx={{
                              bgcolor: isSelected ? 'white' : 'rgba(255,255,255,0.1)',
                              color: isSelected ? '#8E603C' : 'white',
                              '&:hover': {
                                bgcolor: isBlocked ? 'rgba(255,255,255,0.1)' :
                                        isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)',
                              },
                              cursor: isBlocked ? 'not-allowed' : 'pointer',
                              fontWeight: isSelected ? 'bold' : 'normal',
                              height: '28px',
                              opacity: isBlocked ? 0.5 : 1
                            }}
                          />
                        );

                        return isBlocked ? (
                          <Tooltip 
                            key={time}
                            title={t('date_picker.blocked_time_tooltip', 'Denna tid har redan diskuterats')}
                            arrow
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>{chipElement}</span>
                          </Tooltip>
                        ) : chipElement;
                      })}
                      
                      {/* Collapse button */}
                      <Chip
                        label="Show less ▴"
                        size="small"
                        onClick={() => toggleExpanded(dateGroup.date)}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.8)',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.2)',
                          },
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          height: '28px'
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}

        {/* Load More Button - always visible at the end */}
        <Box sx={{ mt: 3, mb: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => setAdditionalWeeks(prev => prev + 1)}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.8)',
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            {t('date_picker.load_more_week', 'Visa en vecka till')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
} 
